/* eslint-disable @typescript-eslint/no-explicit-any */
import { DEFAULT_AXIS_LABEL_FONT_SIZE } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import _zip from "lodash/zip";
import { EvaChartStates } from "types/factory";
import { uid } from "utils";
import { seriesMatchesProduct } from "utils/chart/seriesMatchesProduct";

import { AXIS_NAME_TEXT_STYLE } from "components/multiphase-chart/constants/shared.constants";
import getXAxisPositionFromUnit from "components/multiphase-chart/util/getXAxisPositionFromUnit";

import { createChartLocks } from "../locks";
import { mpcXAxisLocks, mpcYAxisLocks } from "../locks/mpcLocks";
import { DEFAULT_Z_INDEX, STATISTICS_Z_INDEX } from "./constants";
import { buildProductStyleMap, getLineStyleForSeries } from "./mpcTranslatorHelpers";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  createLogAxisLabelFormatter,
  formatChartTitleWithSubscript,
  getDefaultXAxisLogOptions,
  getDefaultYAxisLogOptions,
  getLineSeriesMinMax,
  isStatisticsLabel,
  limitYAxesPerSide
} from "./utils";

export function translateRecoveryPVIResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    series,
    typeWellSeries = [],
    logAxis,
    overrideSeries = true,
    product,
    entityKind,
    multiProduct,
    getSeriesStyle
  } = states;

  if (series.length === 0 && typeWellSeries.length === 0) {
    return {};
  }

  const primaryProducts = multiProduct?.properties?.primaryProducts || [];
  const secondaryProducts = multiProduct?.properties?.secondaryProducts || [];
  const allProducts = [...new Set([...primaryProducts, ...secondaryProducts])];

  // Shared with the other MPC translators so "Override Bin Colours" collapses
  // every product to solid here too (EVA-56).
  const productStyleMap = buildProductStyleMap(multiProduct);

  const getAxisMinMax = (seriesData, axisIndex, axisType) => {
    const filteredSeries = seriesData.filter(
      (s) => (axisType === "x" ? s.xAxisIndex ?? 0 : s.yAxisIndex ?? 0) === axisIndex
    );
    return filteredSeries.length > 0 ? getLineSeriesMinMax(filteredSeries) : null;
  };

  const defaultAxisSeries = series.filter(
    (s) => (s.xAxisIndex ?? 0) === 0 && (s.yAxisIndex ?? 0) === 0
  );
  const { xMin, xMax, yMin, yMax } =
    defaultAxisSeries.length > 0
      ? getLineSeriesMinMax(defaultAxisSeries)
      : getLineSeriesMinMax(series);

  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedYMinType,
    lockedYMaxType
  } = createChartLocks({
    type: EvaChart.RecoveryPVI,
    states
  });

  const xAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    nameGap: 27,
    min: lockedXMin,
    max: lockedXMax
  };

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 43,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax
  };

  let multiXAxis = null;
  let multiYAxis = null;

  const axisPreset = {
    mBblPosition: multiProduct?.properties?.isMBblAxisPositionBottom ? "bottom" : "top",
    mMcfPosition: multiProduct?.properties?.isMMcfAxisPositionBottom ? "bottom" : "top"
  } as any;

  if (layout?.xAxis2?.length) {
    multiXAxis = layout.xAxis2.map((axis, i) => {
      const axisMinMax = getAxisMinMax(series, i, "x");
      const axisXMin = axisMinMax?.xMin || xMin;
      const axisXMax = axisMinMax?.xMax || xMax;

      const xAxisPosition = getXAxisPositionFromUnit(axisPreset, axis.title);

      const seriesForThisAxis = series.filter((s) => (s.xAxisIndex ?? 0) === i);
      const axisProduct =
        allProducts.find((p) =>
          seriesForThisAxis.some(
            (s) =>
              s.label === p ||
              s.groupTitle === p ||
              seriesMatchesProduct(s, p, { exactAlias: true })
          )
        ) ?? product;

      const { lockedXMin: axisLockedXMin, lockedXMax: axisLockedXMax } =
        multiProduct?.active
          ? mpcXAxisLocks(axisProduct, states)
          : { lockedXMin: lockedXMin, lockedXMax: lockedXMax };

      return {
        id: i,
        type: logAxis?.properties?.x?.active ? "log" : "value",
        min: axisLockedXMin,
        max: axisLockedXMax,
        name: formatChartTitleWithSubscript(axis.title),
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: AXIS_NAME_TEXT_STYLE,
        axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE },
        offset: 0,
        position: xAxisPosition,
        axisLine: {
          onZero: false
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true,
          productKey: multiProduct?.active ? axisProduct : undefined,
          min: {
            value: Math.floor(axisLockedXMin ?? axisXMin?.value ?? xMin?.value ?? 0),
            type: axisXMin?.type || xMin.type,
            override: axisLockedXMin !== undefined
          },
          max: {
            value: Math.floor(axisLockedXMax ?? axisXMax?.value ?? xMax?.value ?? 0),
            type: axisXMax?.type || xMax.type,
            override: axisLockedXMax !== undefined
          }
        }
      };
    });
  }

  if (layout?.yAxis2?.length) {
    multiYAxis = layout.yAxis2.map((axis, i) => {
      const axisMinMax = getAxisMinMax(series, i, "y");
      const axisYMin = axisMinMax?.yMin || yMin;
      const axisYMax = axisMinMax?.yMax || yMax;

      const seriesForThisAxis = series.filter((s) => (s.yAxisIndex ?? 0) === i);

      const axisProduct =
        allProducts.find((p) =>
          seriesForThisAxis.some(
            (s) =>
              s.label === p ||
              s.groupTitle === p ||
              seriesMatchesProduct(s, p, { exactAlias: true })
          )
        ) ?? product;

      const {
        lockedYMin: axisLockedYMin,
        lockedYMax: axisLockedYMax,
        lockedYMinType: axisLockedYMinType,
        lockedYMaxType: axisLockedYMaxType
      } = multiProduct?.active
        ? mpcYAxisLocks(axisProduct, seriesForThisAxis, states)
        : {
            lockedYMin: lockedYMin,
            lockedYMax: lockedYMax,
            lockedYMinType: lockedYMinType,
            lockedYMaxType: lockedYMaxType
          };

      const hasAnyPrimary = primaryProducts?.some((p) =>
        seriesForThisAxis.some((s) => seriesMatchesProduct(s, p, { exactAlias: true }))
      );
      const isSecondaryAxis =
        !hasAnyPrimary &&
        secondaryProducts?.some((p) =>
          seriesForThisAxis.some((s) => seriesMatchesProduct(s, p, { exactAlias: true }))
        );
      const axisPosition = isSecondaryAxis ? "right" : "left";

      const isLogY = logAxis?.properties?.y?.active;

      return {
        id: i,
        type: isLogY ? "log" : "value",
        min: axisLockedYMin,
        max: axisLockedYMax,
        name: formatChartTitleWithSubscript(axis.title),
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: AXIS_NAME_TEXT_STYLE,
        axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE },
        position: axisPosition,
        offset: 0,
        axisLine: {
          onZero: false
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true,
          productKey: multiProduct?.active ? axisProduct : undefined,
          min: {
            value: Math.floor(axisLockedYMin ?? axisYMin?.value ?? yMin?.value ?? 0),
            type: axisLockedYMinType ?? (axisYMin?.type || yMin.type),
            override: true
          },
          max: {
            value: Math.floor(axisLockedYMax ?? axisYMax?.value ?? yMax?.value ?? 0),
            type: axisLockedYMaxType ?? (axisYMax?.type || yMax.type),
            override: true
          }
        }
      };
    });
  }

  const yAxisIndexRemap = new Map<number, number>();
  if (multiYAxis) {
    const usedIndices = new Set(series.map((s) => s.yAxisIndex ?? 0));
    const usedAxes = multiYAxis.filter((axis) => usedIndices.has(axis.id as number));
    multiYAxis = limitYAxesPerSide(usedAxes);
    multiYAxis.forEach((axis, newIndex) => {
      yAxisIndexRemap.set(axis.id as number, newIndex);
    });
  }

  if (logAxis?.properties?.x?.active) {
    const xAxisLogOptions = getDefaultXAxisLogOptions(lockedXMin, lockedXMax);
    Object.assign(xAxis, xAxisLogOptions);

    if (multiXAxis) {
      multiXAxis.forEach((axis) => {
        const axisLogOptions = getDefaultXAxisLogOptions(axis.min, axis.max);
        Object.assign(axis, axisLogOptions);
      });
    }
  }

  if (logAxis?.properties?.y?.active) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.RecoveryPVI,
      product,
      entityKind
    );
    Object.assign(yAxis, yAxisLogOptions);

    if (multiYAxis) {
      multiYAxis.forEach((axis) => {
        const perAxisLogOptions = getDefaultYAxisLogOptions(
          layout,
          axis.min,
          axis.max,
          EvaChart.RecoveryPVI,
          product,
          entityKind
        );
        Object.assign(axis, perAxisLogOptions);
        axis.axisLabel = {
          ...axis.axisLabel,
          hideOverlap: true,
          formatter: createLogAxisLabelFormatter(axis.min, axis.max)
        };
      });
    }
  }

  // The server now embeds the radius in the title (e.g. "Oil + Cond RF vs PVI (Gross radius)"),
  // so we use it directly rather than appending client-side.
  let options = createEntityState(ChartOption.Chart, {
    chartType: EvaChart.RecoveryPVI,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis:
      layout?.yAxis2?.length > 0
        ? multiYAxis
        : {
            ...createEntityState(ChartOption.YAxis, {
              ...yAxis,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              lock: {
                show: true,
                min: {
                  value: Math.floor(yMin.value),
                  type: lockedYMinType ?? yMin.type,
                  override: true
                },
                max: {
                  value: Math.floor(yMax.value),
                  type: lockedYMaxType ?? yMax.type,
                  override: true
                }
              }
            })
          },
    xAxis:
      layout?.xAxis2?.length > 0
        ? multiXAxis
        : {
            ...createEntityState(ChartOption.XAxis, {
              ...xAxis,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              lock: {
                show: true,
                min: { value: Math.floor(xMin.value), type: xMin.type },
                max: { value: Math.floor(xMax.value), type: xMax.type }
              }
            })
          },
    grid: { top: 46, bottom: allProducts.length > 0 ? 70 : 46, left: 62, right: 20 },
    ...(multiProduct?.active && {
      dataZoom: [
        { type: "inside", orient: "horizontal", filterMode: "none" },
        { type: "inside", orient: "vertical", filterMode: "none" }
      ]
    }),
    ...(overrideSeries && {
      series: series.map((location, index) => {
        const { label, style, x, y, groupTitle } = location;
        const data = _zip(x, y);
        const id =
          groupTitle !== label
            ? `${groupTitle}, ${label}, ${index}, ${uid()}`
            : `${groupTitle}, ${index}, ${uid()}`;

        const isPoint = data.length === 1;
        const multiPoint = data.length > 1;
        const isStatisticsSeries = isStatisticsLabel(label);
        const { thickness, opacity } = getSeriesStyle(label, style);

        let lineType: any;
        if (location.isForecast) {
          lineType = location.isComparisonForecast ? "dotted" : "dashed";
        } else {
          lineType = getLineStyleForSeries(productStyleMap, location, false, false);
        }

        const lineSeries = createEntityState(ChartOption.LineSeries, {
          id,
          name: label,
          data: data,
          itemStyle: {
            color: style?.hexColor ?? "#000"
          },
          xAxisIndex: location.xAxisIndex,
          yAxisIndex: multiYAxis
            ? yAxisIndexRemap.get(location.yAxisIndex ?? 0) ?? 0
            : location.yAxisIndex,
          lineStyle: {
            width: thickness,
            color: style?.hexColor,
            opacity: opacity,
            type: lineType
          },
          z: isStatisticsSeries ? STATISTICS_Z_INDEX : DEFAULT_Z_INDEX,
          ...(isPoint
            ? {
                symbolSize: 5
              }
            : {}),
          ...(isPoint ? { symbol: "circle" } : {}),
          showSymbol: !multiPoint || isPoint
        });

        return lineSeries;
      })
    })
  });

  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }

  return options;
}
